import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import _ from 'lodash'
import Container from '../layout/Container'
import RootStore from '../../store/Root'
import Button from '../utils/Button'

interface Props {
    rootStore: RootStore
}

const WhiteLabelSite: React.FC<Props> = ({ rootStore }) => {
    const { WhiteLabelStore } = rootStore
    const {
        selectedSiteId,
        siteOptions,
        siteConfig,
        availablePersonas,
        assignedPersonas,
        runtimePreview,
        isLoading,
        isSavingSite,
        isSavingPersona,
        isApiError,
        apiErrorMessage,
        successMessage,
        getWhiteLabelSiteOptions,
        setSelectedSite,
        initializeNewSite,
        saveSiteConfig,
        saveAssignedPersona,
        removeAssignedPersona
    } = WhiteLabelStore

    useEffect(() => {
        getWhiteLabelSiteOptions()
    }, [getWhiteLabelSiteOptions])

    const onSiteConfigChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        _.set(siteConfig, name, value)
        if (name === 'site_id') {
            _.set(siteConfig, 'site_key', value)
        }
    }

    const onAssignedPersonaChange = (index: number, field: string, value: string | boolean) => {
        const nextValue = field === 'sort_order' ? parseInt(String(value), 10) || 1 : value
        _.set(assignedPersonas[index], field, nextValue)
    }

    const onSubmitSite = (e: React.FormEvent) => {
        e.preventDefault()
        saveSiteConfig()
    }

    const onAssignPersona = (agent_key: string) => {
        saveAssignedPersona({ agent_key, name: '', status: '', summary: '', thumbnail_url: '', tags: [], is_assigned: true })
    }

    const onSaveAssignedPersona = (index: number) => {
        saveAssignedPersona(assignedPersonas[index])
    }

    const onRemoveAssignedPersona = (agent_key: string) => {
        if (window.confirm('Are you sure you want to remove this QAI persona from the white-label site?')) {
            removeAssignedPersona(agent_key)
        }
    }

    const noop = () => null

    return (
        <Container rootStore={rootStore} redirectIfNotLoggedIn={true}>
            <div className='row py-2'>
                <div className='col-md-8'>
                    <h4>White-Label Site Config</h4>
                    <p className='text-muted mb-0'>
                        Phase 1 controls one FanFavour-powered white-label site, but the data model and assignment flow are prepared for many sites.
                    </p>
                </div>
            </div>

            {isApiError && <div className='alert alert-danger mt-3'>{apiErrorMessage}</div>}
            {!isApiError && successMessage !== '' && <div className='alert alert-success mt-3'>{successMessage}</div>}

            <div className='card mt-3'>
                <div className='card-header'>Site Selector</div>
                <div className='card-body row align-items-end'>
                    <div className='form-group col-md-8 mb-3'>
                        <label className='mb-2'>Selected White-Label Site</label>
                        <select
                            className='form-control'
                            value={selectedSiteId}
                            onChange={(e) => setSelectedSite(e.target.value)}
                        >
                            {siteOptions.map((site) => (
                                <option key={site.site_id} value={site.site_id}>
                                    {site.site_name || site.site_id} {site.site_domain ? `• ${site.site_domain}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='col-md-4 mb-3 text-md-end'>
                        <Button
                            disabled={isSavingSite || isSavingPersona}
                            type='button'
                            title='Create New Site'
                            classes='btn-outline-primary'
                            loading={false}
                            onClick={initializeNewSite}
                            icon={null}
                        />
                    </div>
                </div>
            </div>

            <form className='card mt-3' onSubmit={onSubmitSite}>
                <div className='card-header'>Site Theme & Config</div>
                <div className='card-body row'>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Site ID</label>
                        <input className='form-control' name='site_id' value={siteConfig.site_id} onChange={onSiteConfigChange} required />
                        <div className='small text-muted mt-1'>Use unique alpha-numeric id. Hyphen and underscore are allowed.</div>
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Site Name</label>
                        <input className='form-control' name='site_name' value={siteConfig.site_name} onChange={onSiteConfigChange} required />
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Site Domain</label>
                        <input className='form-control' name='site_domain' value={siteConfig.site_domain} onChange={onSiteConfigChange} required />
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Site Title</label>
                        <input className='form-control' name='site_title' value={siteConfig.site_title} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Status</label>
                        <select className='form-control' name='status' value={siteConfig.status} onChange={onSiteConfigChange}>
                            <option value='draft'>draft</option>
                            <option value='active'>active</option>
                            <option value='paused'>paused</option>
                        </select>
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Logo URL</label>
                        <input className='form-control' name='logo_url' value={siteConfig.logo_url} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-4 mb-3'>
                        <label className='mb-2'>Favicon URL</label>
                        <input className='form-control' name='favicon_url' value={siteConfig.favicon_url} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-6 mb-3'>
                        <label className='mb-2'>Hero Title</label>
                        <input className='form-control' name='hero_title' value={siteConfig.hero_title} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-6 mb-3'>
                        <label className='mb-2'>Hero Subtitle</label>
                        <input className='form-control' name='hero_subtitle' value={siteConfig.hero_subtitle} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-2 mb-3'>
                        <label className='mb-2'>Primary</label>
                        <input className='form-control form-control-color' type='color' name='primary_color' value={siteConfig.primary_color} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-2 mb-3'>
                        <label className='mb-2'>Secondary</label>
                        <input className='form-control form-control-color' type='color' name='secondary_color' value={siteConfig.secondary_color} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-2 mb-3'>
                        <label className='mb-2'>Accent</label>
                        <input className='form-control form-control-color' type='color' name='accent_color' value={siteConfig.accent_color} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-2 mb-3'>
                        <label className='mb-2'>Background</label>
                        <input className='form-control form-control-color' type='color' name='background_color' value={siteConfig.background_color} onChange={onSiteConfigChange} />
                    </div>
                    <div className='form-group col-md-2 mb-3'>
                        <label className='mb-2'>Text</label>
                        <input className='form-control form-control-color' type='color' name='text_color' value={siteConfig.text_color} onChange={onSiteConfigChange} />
                    </div>
                    <div className='col-12 text-end'>
                        <Button
                            disabled={isSavingSite}
                            type='submit'
                            title='Save Site Config'
                            classes='btn-primary'
                            loading={isSavingSite}
                            onClick={noop}
                            icon={null}
                        />
                    </div>
                </div>
            </form>

            <div className='row mt-4'>
                <div className='col-lg-5 mb-4'>
                    <div className='card h-100'>
                        <div className='card-header'>Available QAI Personas</div>
                        <div className='card-body p-0'>
                            <div className='table-responsive'>
                                <table className='table table-hover mb-0'>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading && (
                                            <tr>
                                                <td colSpan={3} className='text-center py-4'>Loading personas...</td>
                                            </tr>
                                        )}
                                        {!isLoading && availablePersonas.length === 0 && (
                                            <tr>
                                                <td colSpan={3} className='text-center py-4'>No cached QAI personas found.</td>
                                            </tr>
                                        )}
                                        {!isLoading && availablePersonas.map((persona) => (
                                            <tr key={persona.agent_key}>
                                                <td>
                                                    <div className='fw-semibold'>{persona.name || persona.agent_key}</div>
                                                    <div className='small text-muted'>{persona.agent_key}</div>
                                                </td>
                                                <td>{persona.status || '-'}</td>
                                                <td className='text-end'>
                                                    <Button
                                                        disabled={isSavingPersona || persona.is_assigned}
                                                        type='button'
                                                        title={persona.is_assigned ? 'Assigned' : 'Assign'}
                                                        classes={persona.is_assigned ? 'btn-secondary' : 'btn-outline-primary'}
                                                        loading={false}
                                                        onClick={() => onAssignPersona(persona.agent_key)}
                                                        icon={null}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-lg-7 mb-4'>
                    <div className='card h-100'>
                        <div className='card-header'>Assigned Personas For Selected Site</div>
                        <div className='card-body'>
                            {assignedPersonas.length === 0 && (
                                <div className='text-muted'>Assign personas from the QAI cache list to the currently selected white-label site.</div>
                            )}
                            {assignedPersonas.map((persona, index) => (
                                <div key={persona.agent_key} className='border rounded p-3 mb-3'>
                                    <div className='row mb-2'>
                                        <div className='col-md-8'>
                                            <div className='fw-semibold'>{persona.qai_model?.name || persona.agent_key}</div>
                                            <div className='small text-muted'>{persona.agent_key}</div>
                                        </div>
                                        <div className='col-md-4 text-md-end'>
                                            <span className='badge bg-info text-dark'>{persona.qai_model?.status || 'unknown'}</span>
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div className='form-group col-md-5 mb-3'>
                                            <label className='mb-2'>Display Name Override</label>
                                            <input
                                                className='form-control'
                                                value={persona.display_name_override || ''}
                                                onChange={(e) => onAssignedPersonaChange(index, 'display_name_override', e.target.value)}
                                            />
                                        </div>
                                        <div className='form-group col-md-5 mb-3'>
                                            <label className='mb-2'>Headline Override</label>
                                            <input
                                                className='form-control'
                                                value={persona.headline_override || ''}
                                                onChange={(e) => onAssignedPersonaChange(index, 'headline_override', e.target.value)}
                                            />
                                        </div>
                                        <div className='form-group col-md-2 mb-3'>
                                            <label className='mb-2'>Order</label>
                                            <input
                                                className='form-control'
                                                type='number'
                                                min='1'
                                                value={persona.sort_order}
                                                onChange={(e) => onAssignedPersonaChange(index, 'sort_order', e.target.value)}
                                            />
                                        </div>
                                        <div className='form-group col-md-3 mb-3'>
                                            <label className='mb-2'>Active</label>
                                            <select
                                                className='form-control'
                                                value={persona.is_active ? 'true' : 'false'}
                                                onChange={(e) => onAssignedPersonaChange(index, 'is_active', e.target.value === 'true')}
                                            >
                                                <option value='true'>Yes</option>
                                                <option value='false'>No</option>
                                            </select>
                                        </div>
                                        <div className='form-group col-md-3 mb-3'>
                                            <label className='mb-2'>Featured</label>
                                            <select
                                                className='form-control'
                                                value={persona.is_featured ? 'true' : 'false'}
                                                onChange={(e) => onAssignedPersonaChange(index, 'is_featured', e.target.value === 'true')}
                                            >
                                                <option value='false'>No</option>
                                                <option value='true'>Yes</option>
                                            </select>
                                        </div>
                                        <div className='col-md-6 d-flex align-items-end justify-content-end mb-3'>
                                            <Button
                                                disabled={isSavingPersona}
                                                type='button'
                                                title='Save Persona'
                                                classes='btn-outline-primary me-2'
                                                loading={false}
                                                onClick={() => onSaveAssignedPersona(index)}
                                                icon={null}
                                            />
                                            <Button
                                                disabled={isSavingPersona}
                                                type='button'
                                                title='Remove'
                                                classes='btn-outline-danger'
                                                loading={false}
                                                onClick={() => onRemoveAssignedPersona(persona.agent_key)}
                                                icon={null}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className='card mb-4'>
                <div className='card-header'>Runtime Payload Preview For FanFavour</div>
                <div className='card-body'>
                    <p className='text-muted'>
                        FanFavour Phase 1 should consume this config payload for one white-label site instead of hardcoded theme or persona data.
                    </p>
                    <pre className='bg-light border rounded p-3 small overflow-auto mb-0' style={{ maxHeight: '420px' }}>
                        {JSON.stringify(runtimePreview || {}, null, 2)}
                    </pre>
                </div>
            </div>
        </Container>
    )
}

export default observer(WhiteLabelSite)
